import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAppStore } from '@/stores/appStore';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import { Check, CreditCard, Globe, Zap, Building2, AlertCircle, Landmark } from 'lucide-react';
import { useLocationCurrency } from '@/hooks/useLocationCurrency';

type BillingCycle = 'monthly' | 'yearly';

interface PlanDetails {
  key: 'free' | 'basic' | 'intermediate' | 'advanced';
  name: string;
  price: number;
  priceUsd: string;
  description: string;
  features: string[];
  maxUsers: number;
  memberPortal: boolean;
  popular?: boolean;
}

const plans: PlanDetails[] = [
  {
    key: 'free',
    name: 'Free',
    price: 0,
    priceUsd: '$0',
    description: 'Perfect for small groups getting started',
    features: [
      'Attendance taking & tracking',
      'Membership status reports',
      'Up to 20 members',
      'Basic support',
    ],
    maxUsers: 20,
    memberPortal: false,
  },
  {
    key: 'basic',
    name: 'Basic',
    price: 5,
    priceUsd: '$5.50',
    description: 'For growing organizations',
    features: [
      'Everything in Free',
      'Monthly dues tracking',
      'Donations management',
      'All reports (attendance & financial)',
      'Up to 50 members',
      'Email support',
    ],
    maxUsers: 50,
    memberPortal: false,
  },
  {
    key: 'intermediate',
    name: 'Intermediate',
    price: 10,
    priceUsd: '$11',
    description: 'With Member Portal access',
    features: [
      'Everything in Basic',
      'Member Portal with join code',
      'Members can view their records',
      'Up to 80 members',
      'Priority support',
    ],
    maxUsers: 80,
    memberPortal: true,
    popular: true,
  },
  {
    key: 'advanced',
    name: 'Advanced',
    price: 15,
    priceUsd: '$16.50',
    description: 'For larger organizations',
    features: [
      'Everything in Intermediate',
      'Up to 200 members',
      'Advanced analytics',
      'Dedicated support',
      'Custom integrations',
    ],
    maxUsers: 200,
    memberPortal: true,
  },
];

// Fixed regional pricing — not conversions, these are exact prices per region
const regionalPricing: Record<string, Record<string, { price: number; currency: string }>> = {
  NGN: {
    free:         { price: 0,    currency: '₦'   },
    basic:        { price: 2500, currency: '₦'   },
    intermediate: { price: 5000, currency: '₦'   },
    advanced:     { price: 7500, currency: '₦'   },
  },
  GHS: {
    free:         { price: 0,  currency: 'GH₵' },
    basic:        { price: 30, currency: 'GH₵' },
    intermediate: { price: 60, currency: 'GH₵' },
    advanced:     { price: 90, currency: 'GH₵' },
  },
  KES: {
    free:         { price: 0,    currency: 'KSh' },
    basic:        { price: 350,  currency: 'KSh' },
    intermediate: { price: 700,  currency: 'KSh' },
    advanced:     { price: 1050, currency: 'KSh' },
  },
  ZAR: {
    free:         { price: 0,   currency: 'R' },
    basic:        { price: 50,  currency: 'R' },
    intermediate: { price: 100, currency: 'R' },
    advanced:     { price: 150, currency: 'R' },
  },
  USD: {
    free:         { price: 0,  currency: '$' },
    basic:        { price: 5,  currency: '$' },
    intermediate: { price: 10, currency: '$' },
    advanced:     { price: 15, currency: '$' },
  },
  EUR: {
    free:         { price: 0,  currency: '€' },
    basic:        { price: 5,  currency: '€' },
    intermediate: { price: 10, currency: '€' },
    advanced:     { price: 15, currency: '€' },
  },
  GBP: {
    free:         { price: 0,  currency: '£' },
    basic:        { price: 4,  currency: '£' },
    intermediate: { price: 8,  currency: '£' },
    advanced:     { price: 12, currency: '£' },
  },
  CAD: {
    free:         { price: 0,  currency: 'C$' },
    basic:        { price: 7,  currency: 'C$' },
    intermediate: { price: 14, currency: 'C$' },
    advanced:     { price: 21, currency: 'C$' },
  },
  AUD: {
    free:         { price: 0,  currency: 'A$' },
    basic:        { price: 8,  currency: 'A$' },
    intermediate: { price: 16, currency: 'A$' },
    advanced:     { price: 24, currency: 'A$' },
  },
  INR: {
    free:         { price: 0,    currency: '₹' },
    basic:        { price: 420,  currency: '₹' },
    intermediate: { price: 840,  currency: '₹' },
    advanced:     { price: 1260, currency: '₹' },
  },
  BRL: {
    free:         { price: 0,  currency: 'R$' },
    basic:        { price: 25, currency: 'R$' },
    intermediate: { price: 50, currency: 'R$' },
    advanced:     { price: 75, currency: 'R$' },
  },
  MXN: {
    free:         { price: 0,   currency: 'MX$' },
    basic:        { price: 90,  currency: 'MX$' },
    intermediate: { price: 180, currency: 'MX$' },
    advanced:     { price: 270, currency: 'MX$' },
  },
  JPY: {
    free:         { price: 0,    currency: '¥' },
    basic:        { price: 750,  currency: '¥' },
    intermediate: { price: 1500, currency: '¥' },
    advanced:     { price: 2250, currency: '¥' },
  },
  CNY: {
    free:         { price: 0,   currency: 'CN¥' },
    basic:        { price: 36,  currency: 'CN¥' },
    intermediate: { price: 72,  currency: 'CN¥' },
    advanced:     { price: 108, currency: 'CN¥' },
  },
  UGX: {
    free:         { price: 0,     currency: 'USh' },
    basic:        { price: 18500, currency: 'USh' },
    intermediate: { price: 37000, currency: 'USh' },
    advanced:     { price: 55500, currency: 'USh' },
  },
  TZS: {
    free:         { price: 0,     currency: 'TSh' },
    basic:        { price: 13000, currency: 'TSh' },
    intermediate: { price: 26000, currency: 'TSh' },
    advanced:     { price: 39000, currency: 'TSh' },
  },
  XOF: {
    free:         { price: 0,    currency: 'CFA'  },
    basic:        { price: 3000, currency: 'CFA'  },
    intermediate: { price: 6000, currency: 'CFA'  },
    advanced:     { price: 9000, currency: 'CFA'  },
  },
  XAF: {
    free:         { price: 0,    currency: 'FCFA' },
    basic:        { price: 3000, currency: 'FCFA' },
    intermediate: { price: 6000, currency: 'FCFA' },
    advanced:     { price: 9000, currency: 'FCFA' },
  },
};

const AFRICAN_CURRENCIES = ['NGN', 'GHS', 'KES', 'ZAR', 'UGX', 'TZS', 'XOF', 'XAF'] as const;
type AfricanCurrency = typeof AFRICAN_CURRENCIES[number];

// Yearly = 10 months price (2 months free)
const YEARLY_MULTIPLIER = 10;

export default function Subscription() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, currentOrganization } = useAppStore();
  const [isLoading, setIsLoading] = useState(true);
  const [currentPlan, setCurrentPlan] = useState<'free' | 'basic' | 'intermediate' | 'advanced'>('free');
  const [billingCycle, setBillingCycle] = useState<BillingCycle>('monthly');
  const { currency: detectedCurrency } = useLocationCurrency('EUR');

  const fetchCurrentSubscription = React.useCallback(async () => {
    if (!currentOrganization) {
      setIsLoading(false);
      return;
    }

    try {
      setCurrentPlan(currentOrganization.subscription_plan || 'free');
    } catch (error) {
      console.error('Error fetching subscription:', error);
    } finally {
      setIsLoading(false);
    }
  }, [currentOrganization]);

  useEffect(() => {
    fetchCurrentSubscription();
  }, [currentOrganization, fetchCurrentSubscription]);

  /**
   * Resolve the currency to display prices in:
   * 1. Use the org's saved currency if it has a pricing entry
   * 2. Fall back to IP-detected location currency
   * 3. Final fallback: EUR
   */
  const getActiveCurrency = (): string => {
    const orgCurrency = currentOrganization?.currency;
    if (orgCurrency && regionalPricing[orgCurrency]) return orgCurrency;
    if (regionalPricing[detectedCurrency]) return detectedCurrency;
    return 'EUR';
  };

  const getMonthlyEntry = (plan: PlanDetails) => {
    const currency = getActiveCurrency();
    const pricing = regionalPricing[currency] ?? regionalPricing['EUR'];
    return pricing?.[plan.key] ?? { price: plan.price, currency: '€' };
  };

  const getPlanPrice = (plan: PlanDetails): { display: string; perMonth?: string } => {
    const entry = getMonthlyEntry(plan);
    if (entry.price === 0) return { display: `${entry.currency}0` };

    if (billingCycle === 'yearly') {
      const yearly = entry.price * YEARLY_MULTIPLIER;
      return {
        display: `${entry.currency}${yearly.toLocaleString()}`,
        perMonth: `${entry.currency}${(yearly / 12).toLocaleString(undefined, { maximumFractionDigits: 0 })}/mo`,
      };
    }

    return { display: `${entry.currency}${entry.price.toLocaleString()}` };
  };

  const handleSubscribe = async (planKey: 'free' | 'basic' | 'intermediate' | 'advanced') => {
    if (!user || !currentOrganization) {
      toast.error('Please sign in and select an organization');
      return;
    }

    const currency = currentOrganization.currency as string;
    const isAfricanCurrency = AFRICAN_CURRENCIES.includes(currency as AfricanCurrency);

    if (planKey === 'free') {
      try {
        const { error } = await supabase
          .from('organizations')
          .update({
            subscription_plan: 'free',
            subscription_status: 'active',
            max_users: 20,
            member_portal_enabled: false,
          })
          .eq('id', currentOrganization.id);

        if (error) throw error;
        toast.success('Subscribed to Free plan successfully!');
        navigate('/dashboard');
      } catch {
        toast.error('Failed to subscribe. Please try again.');
      }
      return;
    }

    navigate('/payment/checkout', {
      state: {
        plan: planKey,
        billingCycle,
        provider: isAfricanCurrency ? 'paystack' : 'stripe',
        currency: currency,
      },
    });
  };

  const handleBankTransfer = (planKey: 'basic' | 'intermediate' | 'advanced') => {
    if (!user || !currentOrganization) {
      toast.error('Please sign in and select an organization');
      return;
    }
    navigate('/payment/bank-transfer', {
      state: {
        plan: planKey,
        billingCycle,
        currency: currentOrganization.currency,
      },
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-12" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
          <Skeleton className="h-96" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('public.pricing.title')}</h1>
          <p className="text-muted-foreground">{t('public.pricing.subtitle')}</p>
        </div>
        {currentPlan !== 'free' && (
          <Badge variant="default" className="bg-green-500">
            <Check className="w-3 h-3 mr-1" />
            Current: {currentPlan.charAt(0).toUpperCase() + currentPlan.slice(1)}
          </Badge>
        )}
      </div>

      {/* Current Plan Alert */}
      {currentPlan !== 'free' && currentOrganization?.subscription_expires_at && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-500 mt-0.5" />
          <div>
            <p className="font-medium text-blue-900">Active Subscription</p>
            <p className="text-sm text-blue-700">
              Your subscription expires on{' '}
              {new Date(currentOrganization.subscription_expires_at).toLocaleDateString()}
            </p>
          </div>
        </div>
      )}

      {/* Billing Cycle Toggle */}
      <div className="flex items-center justify-center gap-3">
        <span
          className={`text-sm font-medium cursor-pointer ${billingCycle === 'monthly' ? 'text-foreground' : 'text-muted-foreground'}`}
          onClick={() => setBillingCycle('monthly')}
        >
          Monthly
        </span>
        <button
          type="button"
          role="switch"
          aria-checked={billingCycle === 'yearly'}
          onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}
          className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            billingCycle === 'yearly' ? 'bg-primary' : 'bg-input'
          }`}
        >
          <span
            className={`inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${
              billingCycle === 'yearly' ? 'translate-x-6' : 'translate-x-1'
            }`}
          />
        </button>
        <span
          className={`text-sm font-medium cursor-pointer flex items-center gap-2 ${billingCycle === 'yearly' ? 'text-foreground' : 'text-muted-foreground'}`}
          onClick={() => setBillingCycle('yearly')}
        >
          Yearly
          <Badge className="bg-green-500 text-white text-xs py-0 px-1.5">
            2 months free
          </Badge>
        </span>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {plans.map((plan) => {
          const priceInfo = getPlanPrice(plan);
          return (
            <Card
              key={plan.key}
              className={`relative flex flex-col ${
                currentPlan === plan.key
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border'
              } ${plan.popular ? 'lg:scale-105 shadow-lg' : ''}`}
            >
              {plan.popular && (
                <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-secondary">
                  <Zap className="w-3 h-3 mr-1" />
                  Most Popular
                </Badge>
              )}
              {currentPlan === plan.key && (
                <Badge className="absolute -top-3 right-2 bg-primary">
                  <Check className="w-3 h-3 mr-1" />
                  Current
                </Badge>
              )}

              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{plan.name}</CardTitle>
                <CardDescription>{plan.description}</CardDescription>
                <div className="mt-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-3xl font-bold">{priceInfo.display}</span>
                    <span className="text-muted-foreground">
                      {plan.price === 0 ? '' : billingCycle === 'yearly' ? '/year' : '/month'}
                    </span>
                  </div>
                  {billingCycle === 'yearly' && priceInfo.perMonth && (
                    <p className="text-xs text-muted-foreground mt-1">{priceInfo.perMonth} billed annually</p>
                  )}
                </div>
              </CardHeader>

              <CardContent className="flex-1 flex flex-col">
                <ul className="space-y-2 flex-1">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2 text-sm">
                      <Check className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Building2 className="w-4 h-4" />
                    <span>Up to {plan.maxUsers} users</span>
                  </div>
                  {plan.memberPortal && (
                    <div className="flex items-center gap-2 text-sm text-green-600">
                      <Globe className="w-4 h-4" />
                      <span>Member Portal included</span>
                    </div>
                  )}
                </div>

                <Button
                  className="w-full mt-4"
                  variant={currentPlan === plan.key ? 'outline' : 'default'}
                  disabled={currentPlan === plan.key}
                  onClick={() => handleSubscribe(plan.key)}
                >
                  {currentPlan === plan.key ? (
                    'Current Plan'
                  ) : plan.key === 'free' ? (
                    'Get Started Free'
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Subscribe
                    </>
                  )}
                </Button>
                {plan.key !== 'free' && currentPlan !== plan.key && (
                  <Button
                    className="w-full mt-2"
                    variant="outline"
                    onClick={() => handleBankTransfer(plan.key as 'basic' | 'intermediate' | 'advanced')}
                  >
                    <Landmark className="w-4 h-4 mr-2" />
                    Pay by Bank Transfer
                  </Button>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Custom Plan CTA */}
      <Card className="bg-muted/50">
        <CardContent className="p-6 flex flex-col md:flex-row items-center justify-between gap-4">
          <div>
            <h3 className="font-semibold text-lg">Need more than 200 members?</h3>
            <p className="text-muted-foreground">
              Contact us for a custom enterprise plan tailored to your needs.
            </p>
          </div>
          <Button variant="outline" onClick={() => window.open('mailto:support@tickally.com', '_blank')}>
            Contact Sales
          </Button>
        </CardContent>
      </Card>

      {/* Payment Methods */}
      <div className="text-center text-sm text-muted-foreground">
        <p className="flex items-center justify-center gap-2">
          <Globe className="w-4 h-4" />
          We accept payments via Stripe (Global), Paystack (Africa), or Bank Transfer
        </p>
      </div>
    </div>
  );
}
